import type { ReactElement } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatbotProvider } from './contexts/ChatbotContext';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ChatbotWidget from './components/common/ChatbotWidget';
import ProfileIncompleteBanner from './components/common/ProfileIncompleteBanner';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import FAQs from './pages/FAQs';
import ContactUs from './pages/ContactUs';
import Donation from './pages/Donation';
import SignIn from './pages/SignIn';
import LinkedInCallback from './pages/LinkedInCallback';
import AppleSetup from './pages/AppleSetup';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Profile from './pages/account/Profile';
import ProfileDetails from './pages/account/ProfileDetails';
import CompleteProfile from './pages/account/CompleteProfile';
import MyTasks from './pages/account/MyTasks';
import Payments from './pages/account/Payments';
import Wallet from './pages/account/Wallet';
import Charges from './pages/account/Charges';
import Earnings from './pages/account/Earnings';
import Credits from './pages/account/Credits';
import CreditApply from './pages/account/CreditApply';
import CreditApplications from './pages/account/CreditApplications';
import CreditApplicationDetails from './pages/account/CreditApplicationDetails';
import Inbox from './pages/account/Inbox';
import EmailDetails from './pages/account/EmailDetails';
import HelpSupport from './pages/account/HelpSupport';
import Settings from './pages/account/Settings';
import BusinessRegistration from './pages/account/BusinessRegistration';
import BusinessEntityProfile from './pages/account/BusinessEntityProfile';
import BusinessLanding from './pages/account/BusinessLanding';

// NO DebugAuth import here - remove if present

function ProtectedRoute({ children }: { children: ReactElement }) {
  const { isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (!isLoggedIn) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
}

const protectedPage = (element: ReactElement) => (
  <ProtectedRoute>{element}</ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <ChatbotProvider>
        <div className="app-wrapper">
          <Navbar />
          <ProfileIncompleteBanner />
          <main className="main-content">
            <Routes>
              {/* Main routes */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              
              {/* About routes */}
              <Route path="/about" element={<AboutUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              
              {/* FAQ routes */}
              <Route path="/faq" element={<FAQs />} />
              <Route path="/faqs" element={<FAQs />} />
              
              {/* Contact routes */}
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/contact-us" element={<ContactUs />} />
              
              {/* Donation routes */}
              <Route path="/donate" element={<Donation />} />
              <Route path="/donation" element={<Donation />} />
              
              {/* Auth routes */}
              <Route path="/signin" element={<SignIn />} />
              <Route path="/login" element={<SignIn />} />
              <Route path="/linkedin/callback" element={<LinkedInCallback />} />
              <Route path="/apple-setup" element={<AppleSetup />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/register" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              {/* Account routes */}
              <Route path="/account/profile" element={protectedPage(<Profile />)} />
              <Route path="/account/profile-details" element={protectedPage(<ProfileDetails />)} />
              <Route path="/account/complete" element={protectedPage(<CompleteProfile />)} />
              <Route path="/account/tasks" element={protectedPage(<MyTasks />)} />
              <Route path="/account/payments" element={protectedPage(<Payments />)} />
              <Route path="/account/payments/wallet" element={protectedPage(<Wallet />)} />
              <Route path="/account/payments/charges" element={protectedPage(<Charges />)} />
              <Route path="/account/payments/earnings" element={protectedPage(<Earnings />)} />
              <Route path="/account/credits" element={protectedPage(<Credits />)} />
              <Route path="/account/credits/apply" element={protectedPage(<CreditApply />)} />
              <Route path="/account/credits/applications" element={protectedPage(<CreditApplications />)} />
              <Route path="/account/credits/applications/:applicationId" element={protectedPage(<CreditApplicationDetails />)} />
              <Route path="/account/inbox" element={protectedPage(<Inbox />)} />
              <Route path="/account/inbox/:emailId" element={protectedPage(<EmailDetails />)} />
              <Route path="/account/help-support" element={protectedPage(<HelpSupport />)} />
              <Route path="/account/support" element={protectedPage(<HelpSupport />)} />
              <Route path="/account/feedback" element={protectedPage(<HelpSupport />)} />
              <Route path="/account/settings" element={protectedPage(<Settings />)} />
              <Route path="/account/business-registration" element={protectedPage(<BusinessRegistration />)} />
              <Route path="/account/business" element={protectedPage(<BusinessLanding />)} />
              <Route path="/account/business/my" element={protectedPage(<BusinessLanding />)} />
              <Route path="/account/business/register" element={protectedPage(<BusinessRegistration />)} />
              <Route path="/account/business/:businessEntityId" element={protectedPage(<BusinessEntityProfile />)} />
              
              {/* Legal routes */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-of-use" element={<TermsOfService />} />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <ChatbotWidget />
        </div>
      </ChatbotProvider>
    </AuthProvider>
  );
}

export default App;
